/**
 * A Loader that shows an animation and rotates through messages for requests that take longer than most,
 * and where a normal spinner won't suffice. Operates similarly to the Overpanel component used in transfer/.
 * Just pass true or false as prop show, and messages under messages prop.
 */

import PropTypes from 'prop-types'

import React from 'react'
import content from 'pp-react-l10n'
import { LoadingSpinner } from '@paypalcorp/pp-react'
import ReactDOM, {
  unstable_renderSubtreeIntoContainer as renderSubtreeIntoContainer,
} from 'react-dom'
import classNames from 'classnames'
import { css } from 'lib/paypalphrodite'
import invariant from 'invariant'
import styles from './loader.styles'
import _ from 'lodash'
import serverProps from '../utils/serverProps'

const i18n = content('transfer/loader')

const CDN_HOST_NAME = _.get(serverProps, 'cdnHostName')

class Loader extends React.Component {
  static propTypes = {
    show: PropTypes.bool.isRequired,
    messages: PropTypes.array,
    background: PropTypes.object,
  }

  state = {
    removeFromFlow: true,
    show: false,
    messageIdx: 0,
  }

  componentDidMount() {
    if (!this.node) {
      this.node = document.createElement('div')
      this.node.className = 'react-Loader'
      document.body.appendChild(this.node)
    }

    this.node.addEventListener('animationend', this.handleAnimationEnd, false)
    this.node.addEventListener(
      'mozAnimationEnd',
      this.handleAnimationEnd,
      false
    )
    this.node.addEventListener(
      'webkitAnimationEnd',
      this.handleAnimationEnd,
      false
    )
    this.node.addEventListener('msAnimationEnd', this.handleAnimationEnd, false)

    if (this.props.show) {
      this.show()
    }

    this.renderLoader(this.props, this.state)
  }

  componentWillUnmount() {
    if (this.interval) {
      clearInterval(this.interval)
    }

    ReactDOM.unmountComponentAtNode(this.node)
    document.body.removeChild(this.node)
    this.node.removeEventListener(
      'animationend',
      this.handleAnimationEnd,
      false
    )
    this.node.removeEventListener(
      'mozAnimationEnd',
      this.handleAnimationEnd,
      false
    )
    this.node.removeEventListener(
      'webkitAnimationEnd',
      this.handleAnimationEnd,
      false
    )
    this.node.removeEventListener(
      'msAnimationEnd',
      this.handleAnimationEnd,
      false
    )
  }

  handleAnimationEnd = () => {
    if (!this.state.show) {
      // the loader has finished fading out — let's remove it from flow, so it doesn't block clicks
      this.setState({
        removeFromFlow: true,
      })
    }
  }

  tick = () => {
    const newIdx =
      this.state.messageIdx + 1 < this.props.messages.length
        ? this.state.messageIdx + 1
        : 0
    this.setState({
      messageIdx: newIdx,
    })
  }

  show = () => {
    if (this.props.messages) {
      this.interval = setInterval(this.tick, 4000)
    }
    this.setState({
      show: true,
      removeFromFlow: false,
    })
  }

  hide = () => {
    if (this.props.messages) {
      clearInterval(this.interval)
    }
    this.setState({
      show: false,
      // is removed from flow after on animationend
    })
  }

  componentWillReceiveProps(newProps) {
    if (newProps.show) {
      this.show()
    } else {
      this.hide()
    }
  }

  // Putting OUR custom render method here allows us to re-render as if it were in the normal render method. It has access
  // to the most updated state (including anything set in componentWillReceiveProps), as well, through nextState.
  componentWillUpdate(nextProps, nextState) {
    this.renderLoader(nextProps, nextState)
  }

  renderLoader = (props, state) => {
    renderSubtreeIntoContainer(
      this,
      this.renderContent(props, state),
      this.node
    )
  }

  renderSpecialLoader = (props, state) => {
    return (
      <div className={css(styles.centeredContent)}>
        {/* The wrapper around the image is so that the layout doesn't pop around if the image loads late */}
        <div style={{ width: 85, height: 85, margin: '0 auto' }}>
          <img
            alt=""
            width="85px"
            src={`${CDN_HOST_NAME}/images/p2p/guest_preloader.gif`}
          />
        </div>
        {/*
          Something like this will be added when the final gif is available. for now just use .spinner.loading
          <img style={{ marginBottom: 25 }} src="https://s31.postimg.org/5f74i4nsb/Test.gif" alt=""/>
        */}
        <p
          style={{ fontSize: 18, marginLeft: 13 }}
          className={css(styles.pulse)}
        >
          {props.messages[state.messageIdx]}
        </p>
      </div>
    )
  }

  renderBasicLoader = (props, state) => {
    return (
      <div className={css(styles.basicPosition)}>
        <LoadingSpinner
          screenReaderText={i18n('loading')}
          size="xl"
          data-nemo="p2p-spinner-wait"
        />
      </div>
    )
  }

  renderContent = (props, state) => {
    const wrapperClassNames = {
      hide: state.removeFromFlow,
    }

    const fadeAnimation = state.show ? 'fadeIn' : 'fadeOut'

    const background = this.props.background || styles.background

    // warn when array is passed
    invariant(
      !Array.isArray(props.children),
      '<Loader /> child: Adjacent JSX elements must be wrapped in an enclosing tag. Please wrap the <Loader /> children in a single tag.'
    )

    return (
      <div
        className={classNames(
          css(styles.wrapper),
          fadeAnimation,
          wrapperClassNames
        )}
      >
        <div className={css(background)}>
          {this.props.messages
            ? this.renderSpecialLoader(props, state)
            : this.renderBasicLoader(props, state)}
        </div>
      </div>
    )
  }

  // Must be empty so we can render the Loader, with our custom method, as a child of document.body
  render() {
    return null
  }
}

export default Loader
